import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ContentText from '../../Common/ContentText';
import { FeatureData } from './types';
import styles from './FeatureCard.module.css';

interface FeatureCardProps {
  feature: FeatureData;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  feature,
  isExpanded,
  onToggleExpand
}) => {
  const { id, name, description, type, uses, progression, cost, availableLevels } = feature;

  // Get feature type badge(s)
  const getTypeBadge = () => {
    const badges = [];

    // Origin class badge FIRST (shows which class it comes from - for origin features)
    if (!type.startsWith('multiclass-') && feature.className) {
      badges.push(
        <span key="origin-class" className={styles.badge} data-type="origin-class">
          {feature.className}
        </span>
      );
    }

    // Multiclass source badge FIRST (shows which class it comes from - for multiclass features)
    if (type.startsWith('multiclass-') && feature.multiclassData) {
      badges.push(
        <span key="multiclass" className={styles.badge} data-type="multiclass">
          Multiclass: {feature.multiclassData.className}
        </span>
      );
    }

    // Feature type badge (shows what kind of feature it is)
    if (type === 'class' || type === 'multiclass-class-feature') {
      badges.push(
        <span key="type" className={styles.badge} data-type="class">
          Class Feature
        </span>
      );
    } else if (type === 'multiclass-foundation' || type === 'subclass-foundation') {
      if (feature.subclassName) {
        badges.push(
          <span key="subclass" className={styles.badge} data-type="subclass-name">
            {feature.subclassName}
          </span>
        );
      }
      badges.push(
        <span key="type" className={styles.badge} data-type="subclass">
          Foundation
        </span>
      );
    } else if (type === 'subclass-specialization') {
      if (feature.subclassName) {
        badges.push(
          <span key="subclass" className={styles.badge} data-type="subclass-name">
            {feature.subclassName}
          </span>
        );
      }
      badges.push(
        <span key="type" className={styles.badge} data-type="subclass">
          Specialization
        </span>
      );
    } else if (type === 'subclass-mastery') {
      if (feature.subclassName) {
        badges.push(
          <span key="subclass" className={styles.badge} data-type="subclass-name">
            {feature.subclassName}
          </span>
        );
      }
      badges.push(
        <span key="type" className={styles.badge} data-type="subclass">
          Mastery
        </span>
      );
    } else if (type === 'multiclass-domain-access') {
      badges.push(
        <span key="type" className={styles.badge} data-type="domain">
          Domain Access
        </span>
      );
    } else if (type === 'hope') {
      badges.push(
        <span key="type" className={styles.badge} data-type="hope">
          Hope Feature
        </span>
      );
    } else if (type === 'tier-advancement') {
      badges.push(
        <span key="type" className={styles.badge} data-type="tier">
          Tier Advancement
        </span>
      );
    }

    return badges.length > 0 ? <>{badges}</> : null;
  };

  return (
    <motion.div
      className={styles.featureCard}
      data-feature-type={type}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      layout
      onClick={onToggleExpand}
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      aria-controls={`feature-content-${id}`}
      aria-label={`${name}. ${isExpanded ? 'Collapse' : 'Expand'} to ${isExpanded ? 'hide' : 'show'} details.`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggleExpand();
        }
      }}
    >
      <motion.div
        className={styles.header}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
      >
        <div className={styles.titleRow}>
          <h4 className={styles.title}>
            <span className={styles.expandIcon} aria-hidden="true">
              {isExpanded ? '▼' : '▶'}
            </span>
            {name}
          </h4>
          <div className={styles.badgeContainer}>
            {getTypeBadge()}
          </div>
        </div>
        {uses && !isExpanded && (
          <div className={styles.usesPreview} aria-label={`Uses: ${uses}`}>{uses}</div>
        )}
      </motion.div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            id={`feature-content-${id}`}
            className={styles.content}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            role="region"
            aria-label={`${name} details`}
          >
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <ContentText content={description} />
              {uses && (
                <p className={styles.uses}>
                  <strong>Uses:</strong> {uses}
                </p>
              )}
              {cost !== undefined && (
                <p className={styles.hopeCost}>
                  <strong>Hope Cost:</strong> {cost}
                </p>
              )}
              {availableLevels && availableLevels.length > 0 && (
                <div className={styles.availableLevels}>
                  <strong>Available Levels:</strong>
                  <div className={styles.levelBadges}>
                    {availableLevels.map((level) => (
                      <span key={level} className={`${styles.levelBadge} ${styles.unlocked}`}>
                        {level}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {progression && (
                <p className={styles.progression}>
                  <em>{progression}</em>
                </p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default FeatureCard;
